import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  MapPin, Clock, Phone, User, Package, CheckCircle, 
  Truck, Navigation, RefreshCw, Loader2, Search
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function OrderTracking() {
  const { orderId: paramOrderId } = useParams();
  const [searchParams] = useSearchParams();
  const [orderId, setOrderId] = useState(paramOrderId || searchParams.get('order') || '');
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchTracking = async (id) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/customer/order-tracking/${id}`);
      setTracking(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found');
      setTracking(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (paramOrderId) {
      fetchTracking(paramOrderId);
    }
  }, [paramOrderId]);

  useEffect(() => {
    if (!autoRefresh || !tracking) return;
    const interval = setInterval(() => {
      fetchTracking(orderId || paramOrderId);
    }, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [autoRefresh, tracking, orderId, paramOrderId]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTracking(orderId);
  };

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800',
      'confirmed': 'bg-blue-100 text-blue-800',
      'preparing': 'bg-orange-100 text-orange-800',
      'ready': 'bg-green-100 text-green-800',
      'assigned': 'bg-purple-100 text-purple-800',
      'in_transit': 'bg-indigo-100 text-indigo-800',
      'out_for_delivery': 'bg-pink-100 text-pink-800',
      'delivered': 'bg-emerald-100 text-emerald-800',
      'cancelled': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    if (status === 'delivered') return <CheckCircle className="w-5 h-5 text-emerald-600" />;
    if (['in_transit', 'out_for_delivery'].includes(status)) return <Truck className="w-5 h-5 text-indigo-600" />;
    if (status === 'ready') return <Package className="w-5 h-5 text-green-600" />;
    return <Clock className="w-5 h-5 text-yellow-600" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Track Your Order</h1>
          <p className="text-gray-600">Enter your order number to see real-time delivery status</p>
        </div>

        {/* Search Form */}
        <Card className="p-6 mb-6 bg-white shadow-lg">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                value={orderId}
                onChange={(e) => setOrderId(e.target.value)}
                placeholder="Enter Order ID or Order Number"
                className="pl-10 h-12"
                data-testid="order-search-input"
              />
            </div>
            <Button type="submit" className="h-12 px-6 bg-[#0a7d4f] hover:bg-[#086b43]" disabled={loading}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Track'}
            </Button>
          </form>
        </Card>

        {/* Error Message */}
        {error && (
          <Card className="p-6 mb-6 bg-red-50 border-red-200">
            <p className="text-red-600 text-center">{error}</p>
          </Card>
        )}

        {/* Tracking Info */}
        {tracking && (
          <Card className="bg-white shadow-lg overflow-hidden" data-testid="tracking-card">
            {/* Order Header */}
            <div className="p-6 bg-gradient-to-r from-[#0a7d4f] to-[#0d9963] text-white">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm opacity-80">Order Number</p>
                  <p className="text-2xl font-bold">{tracking.order_number}</p>
                </div>
                <div className="flex items-center gap-2">
                  {getStatusIcon(tracking.status)}
                  <Badge className={`${getStatusColor(tracking.status)} px-3 py-1`}>
                    {tracking.status?.replace(/_/g, ' ').toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm opacity-90">
                <MapPin className="w-4 h-4" />
                <span>{tracking.restaurant_name}</span>
              </div>
            </div>

            {/* Delivery Partner Info */}
            {tracking.delivery_partner && (
              <div className="p-4 bg-blue-50 border-b border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {tracking.delivery_partner.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{tracking.delivery_partner.name}</p>
                      <p className="text-sm text-gray-500">Delivery Partner</p>
                    </div>
                  </div>
                  <a 
                    href={`tel:${tracking.delivery_partner.phone}`}
                    className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Phone className="w-4 h-4 text-[#0a7d4f]" />
                    <span className="text-sm font-medium">Call</span>
                  </a>
                </div>
              </div>
            )}

            {/* Live Location */}
            {tracking.live_location && (
              <div className="p-4 bg-green-50 border-b border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <Navigation className="w-5 h-5 text-green-600 animate-pulse" />
                  <span className="font-medium text-green-800">Live Tracking</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-sm text-green-700">
                    <p>Last updated: {new Date(tracking.live_location.updated_at).toLocaleTimeString()}</p>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    className={autoRefresh ? 'bg-green-100' : ''}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
                    {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh'}
                  </Button>
                </div>
              </div>
            )}

            {/* Delivery Address */}
            <div className="p-4 border-b">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Delivering to</p>
                  <p className="font-medium">{tracking.delivery_address}</p>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-600" />
                Order Timeline
              </h3>
              <div className="space-y-4">
                {tracking.timeline?.map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="relative">
                      <div className={`w-3 h-3 rounded-full ${
                        index === tracking.timeline.length - 1 ? 'bg-[#0a7d4f]' : 'bg-gray-300'
                      }`} />
                      {index < tracking.timeline.length - 1 && (
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 w-0.5 h-8 bg-gray-200" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className={`font-medium ${
                        index === tracking.timeline.length - 1 ? 'text-[#0a7d4f]' : 'text-gray-600'
                      }`}>
                        {item.status}
                      </p>
                      {item.time && (
                        <p className="text-sm text-gray-400">
                          {new Date(item.time).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Estimated Delivery */}
            {tracking.estimated_delivery && (
              <div className="p-4 bg-yellow-50 border-t">
                <div className="flex items-center gap-2 text-yellow-800">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">
                    Estimated Delivery: {new Date(tracking.estimated_delivery).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {/* Refresh Button */}
            <div className="p-4 bg-gray-50 border-t">
              <Button 
                variant="outline" 
                className="w-full" 
                onClick={() => fetchTracking(orderId || paramOrderId)}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
            </div>
          </Card>
        )}

        {/* Initial State */}
        {!tracking && !error && !loading && !paramOrderId && (
          <Card className="p-12 bg-white shadow-lg text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">Enter your order number above to track your delivery</p>
          </Card>
        )}
      </div>
    </div>
  );
}

export default OrderTracking;
